import React, { useContext, useMemo } from 'react';
import {
  QuestionCircleOutlined,
  LogoutOutlined,
  SettingOutlined,
  FileSearchOutlined,
  UserOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  Outlet,
  useLocation,
  useNavigate,
  Link,
  useSearchParams,
} from 'react-router-dom';
import { Dropdown, Modal, Layout, Menu, Progress } from 'antd';
import { FeatureKeys } from '@owl-frontend/api-client/interface';
import { Button, AvatarAntd } from '@owl-frontend/components';
import Logo from '../../assets/Owl-Platform-Logo.svg';
import { SessionContext } from '../../context/AppContainer/AppContainer';
import { actions as sessionSliceActions } from '../../data/session/logic';
import type { StoreState } from '../../data/store';
import { getCurrentMonth } from '../../helpers/dates';
import { useIsManager } from '../../helpers/hooks';
import { RouteDefs } from '../Routing/routes';
import SettingsModalComponent from '../SettingsModal/SettingsModalComponent';
import useEmailSubscriptionSettings from '../SettingsModal/useEmailSubscriptionSettings';
import styles from './MainContainer.module.scss';

const BREAK_LINE = '%0D%0A';

const MainContainer = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isManager = useIsManager();
  const messages = useSelector((s: StoreState) => s['localization'].messages);
  const { current: session, features } = useContext(SessionContext);
  const tenant = useSelector((s: StoreState) => s['tenant'].tenant);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = React.useState(false);

  const selectedKeys = React.useMemo(() => {
    return [`/${location.pathname.split('/')[1]}`];
  }, [location]);
  const [emailSubscriptions, updateEmailSubscriptions] =
    useEmailSubscriptionSettings();

  const mailTo = React.useMemo(() => {
    let result = 'mailto:support@owl.co';

    const body = [`Hi ${session?.name ?? ''},`];
    body.push('Thanks so much for taking the time to contact Owl.');
    body.push(
      'So that we can best support you, please share what you are reaching out to us about:'
    );
    const options = [
      '[] Technical support',
      '[] Product inquiry',
      '[] Product feedback',
      '[] Other',
    ].join(BREAK_LINE);
    body.push(options);
    const share = [
      'Please provide a brief description about what you are experiencing:',
      '[Type here]',
    ].join(BREAK_LINE);
    body.push(share);
    const screenshot = [
      'If there are any screenshots you would like to share with us, please feel free to attach them to this e-mail or copy and paste below.',
      '[Insert screenshot]',
    ].join(BREAK_LINE);
    body.push(screenshot);

    body.push(
      'Owl’s Customer Support team will carefully review your feedback and respond as soon as possible.'
    );

    body.push('');

    const sincerely = ['Sincerely,', 'Owl Support Team'].join(BREAK_LINE);
    body.push(sincerely);

    result = `${result}?body=${body.join(`${BREAK_LINE}${BREAK_LINE}`)}`;

    return result;
  }, [session]);

  const onMenuItemClick = React.useCallback(
    ({ key }) => {
      navigate(key);
    },
    [navigate]
  );

  const toggleSettingsModal = React.useCallback(
    (open: boolean) => {
      if (open) {
        searchParams.set('modal', 'settings');
      }
      if (!open) {
        searchParams.delete('modal');
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const menuData = useMemo(() => {
    const result = [
      {
        key: 'support',
        label: <a href={mailTo}>{messages['main.support']}</a>,
        icon: <QuestionCircleOutlined />,
      },
    ];

    const hasEmailsEnabled = Object.keys(features).some(
      (key) => key.endsWith('notify') && features[key]
    );

    if (hasEmailsEnabled) {
      result.push({
        key: 'settings',
        label: (
          <Link
            data-testid="settings-btn"
            to="#"
            onClick={() => toggleSettingsModal(true)}
          >
            {messages['main.settings']}
          </Link>
        ),
        icon: <SettingOutlined />,
      });
    }

    result.push({
      key: 'logout',
      icon: <LogoutOutlined />,
      label: (
        <Link
          data-testid="logout-btn"
          to="#"
          onClick={() => setIsLogoutModalOpen(true)}
        >
          {messages['main.logout']}
        </Link>
      ),
    });
    return result;
  }, [features, mailTo, messages, toggleSettingsModal]);

  const menuItems = [
    {
      key: RouteDefs.Dossiers,
      icon: <FileSearchOutlined />,
      label: messages['dossiers.pathname'],
    },
  ];

  if (isManager) {
    if (features[FeatureKeys.Insights]) {
      menuItems.unshift({
        key: RouteDefs.Insights,
        icon: <DashboardOutlined />,
        label: messages['insights.title'],
      });
    }
    menuItems.push({
      key: RouteDefs.Users,
      icon: <UserOutlined />,
      label: messages['users.title'],
    });
  }

  let numClaimsRemaining = 0;
  const reviewCounter =
    typeof tenant?.maxReviewLimit === 'number' &&
    typeof tenant?.recordsRemaining === 'number';
  if (reviewCounter) {
    numClaimsRemaining =
      tenant.maxReviewLimit - tenant.recordsRemaining < 0
        ? 0
        : tenant.maxReviewLimit - tenant.recordsRemaining;
  }

  const handleLogoutOk = () => {
    dispatch(sessionSliceActions.logout());
    setIsLogoutModalOpen(false);
  };

  const handleLogoutCancel = () => {
    setIsLogoutModalOpen(false);
  };

  return (
    <Layout className={styles.mainContainer}>
      <Layout.Header className={styles.topPanel}>
        <Logo />
        <SettingsModalComponent
          open={searchParams.get('modal') === 'settings'}
          handleOnClose={() => toggleSettingsModal(false)}
          emailSubscriptions={emailSubscriptions}
          updateEmailSubscriptions={updateEmailSubscriptions}
        />
        <Menu
          className={styles.navigation}
          mode="horizontal"
          onClick={onMenuItemClick}
          selectedKeys={selectedKeys}
          items={menuItems}
        />
        <div className={styles.reviewProgressContainerContainer}>
          <div className={styles.reviewProgressContainer}>
            {features[FeatureKeys.ReviewCounter] &&
              reviewCounter &&
              (tenant.recordsRemaining > 0 ? (
                <>
                  <div style={{ fontWeight: 'bold' }}>
                    <FormattedMessage
                      id="reviewLimit.underLimit"
                      values={{
                        recordsRemaining: (
                          <span className={styles.underLimit}>
                            {numClaimsRemaining}
                          </span>
                        ),
                        maxReviewLimit: tenant.maxReviewLimit,
                        month: messages[`months.${getCurrentMonth()}`],
                      }}
                    />
                  </div>
                  <Progress
                    percent={(numClaimsRemaining / tenant.maxReviewLimit) * 100}
                    size="small"
                    type="line"
                  />
                </>
              ) : (
                <div className={styles.overLimitContainer}>
                  <FormattedMessage
                    id="reviewLimit.atLimit"
                    values={{
                      recordsRemaining: (
                        <span className={styles.overLimit}>
                          {numClaimsRemaining}
                        </span>
                      ),
                      maxReviewLimit: tenant.maxReviewLimit,
                      month: messages[`months.${getCurrentMonth()}`],
                      celebratory: '\u{1f389}',
                    }}
                  />
                </div>
              ))}
          </div>
        </div>
        <Dropdown menu={{ items: menuData }} placement="bottomRight">
          <Button
            className={styles.mainDropdown}
            href="#"
            data-testid="user-dropdown"
            shape="circle"
            type="text"
            onClick={(e) => e.preventDefault()}
          >
            <AvatarAntd>
              {session?.name?.toUpperCase().substring(0, 2)}
            </AvatarAntd>
          </Button>
        </Dropdown>
      </Layout.Header>
      <Layout.Content>
        <Outlet />
      </Layout.Content>
      <Modal
        title="Logout"
        open={isLogoutModalOpen}
        onOk={handleLogoutOk}
        onCancel={handleLogoutCancel}
      >
        <p>{messages['shared.confirmPrompt']}</p>
      </Modal>
    </Layout>
  );
};

export default MainContainer;
